import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLang } from '../context/LanguageContext';
import ScrollReveal from '../components/ScrollReveal';
import Seo from '../components/Seo';
import { getFizikselMedyaPage, STRAPI_URL } from '../services/api';
import DynamicIcon from '../components/DynamicIcon';
import DEFAULT_DATA from '../default_datas/pages/defaultFizikselMedya';

const resolveMediaUrl = (mediaField) => {
  if (!mediaField) return null;
  const media = Array.isArray(mediaField) ? mediaField[0] : mediaField;
  const url = media?.url;
  if (!url) return null;
  return url.startsWith('http') ? url : `${STRAPI_URL}${url}`;
};

const FizikselMedya = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { lang } = useLang();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const tx = (tr, en) => (lang === 'en' ? (en || tr) : tr);

  useEffect(() => {
    getFizikselMedyaPage()
      .then((result) => {
        setData(result || null);
      })
      .catch((err) => {
        console.error('Strapi fetch hatası:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-[#2a9d8f] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const sections = data?.pageContent || [];

  const heroSection = sections.find((s) => s.__component === 'sections.hero');
  const statsRaw = sections.find((s) => s.__component === 'sections.stats-media-section');
  const featuresRaw = sections.find((s) => s.__component === 'sections.features-section');
  const ctaRaw = sections.find((s) => s.__component === 'sections.cta');

  const hero = { ...DEFAULT_DATA.hero, ...(heroSection || {}) };
  const statsSection = statsRaw?.stats?.length > 0 ? statsRaw : DEFAULT_DATA.statsSection;
  const features = featuresRaw ? { ...DEFAULT_DATA.features, ...featuresRaw } : DEFAULT_DATA.features;
  const cta = ctaRaw ? { ...DEFAULT_DATA.cta, ...ctaRaw } : DEFAULT_DATA.cta;

  const heroImageUrl = resolveMediaUrl(hero.image);
  const gorselSolda = hero?.gorselPozisyon === 'sol';

  return (
    <>
      <Seo
        title={lang === 'en' ? 'Physical Media Security | RUZY' : 'Fiziksel Medya Güvenliği | RUZY'}
        description={
          lang === 'en'
            ? 'Manage USB drives and physical data carriers in compliance with ISO 27001.'
            : 'Fiziksel veri taşıyıcılarını ISO 27001 uyumlu şekilde yönetin.'
        }
        path="/fiziksel-medya"
      />

      <div className="font-poppins text-[#0D3A2F] overflow-x-hidden">

        {/* ── 1. HERO ── */}
        <ScrollReveal>
          <section
            className={`flex flex-col items-center justify-between px-6 lg:px-28 py-10 lg:py-20 bg-[#f5faf9] gap-10 lg:gap-16 ${gorselSolda ? 'lg:flex-row' : 'lg:flex-row-reverse'
              }`}
          >
            <div className="flex-1 flex justify-center w-full">
              {heroImageUrl ? (
                <img
                  src={heroImageUrl}
                  alt={tx(hero.title, hero.title_en)}
                  className="w-full max-w-[420px] h-[250px] lg:h-auto rounded-2xl object-cover shadow-2xl border border-white"
                />
              ) : (
                <div className="w-full max-w-[420px] h-64 bg-[#e0f2ef] rounded-2xl flex items-center justify-center text-[#2a9d8f] text-sm font-semibold">
                  {lang === 'en' ? 'Image loading...' : 'Görsel yükleniyor...'}
                </div>
              )}
            </div>

            <div className="flex-1 max-w-full lg:max-w-[520px] text-center lg:text-left items-center lg:items-start">
              {hero.badge && (
                <span className="inline-block bg-[#e0f2ef] text-[#2a9d8f] text-[10px] lg:text-xs font-semibold px-4 py-1.5 rounded-full mb-5 tracking-wide uppercase">
                  {tx(hero.badge, hero.badge_en)}
                </span>
              )}

              <h1 className="text-3xl lg:text-[42px] font-bold leading-tight mb-5">
                {tx(hero.title, hero.title_en)}
              </h1>

              <p className="text-sm lg:text-[15px] leading-relaxed text-[#4a6b63] mb-8">
                {tx(hero.description, hero.description_en)}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
                <button
                  onClick={() => navigate('/iletisim')}
                  className="h-12 lg:h-auto bg-[#2a9d8f] text-white border-none px-7 py-3.5 rounded-xl text-sm lg:text-[15px] font-semibold cursor-pointer transition-transform active:scale-95 shadow-lg shadow-green-100"
                >
                  {tx(hero.primaryButtonText, hero.primaryButtonText_en)}
                </button>
                {hero.secondaryButtonText && (
                  <button
                    onClick={() => navigate('/iletisim')}
                    className="h-12 lg:h-auto bg-white text-[#0D3A2F] border-2 border-[#0D3A2F] px-7 py-3.5 rounded-xl text-sm lg:text-[15px] font-semibold cursor-pointer"
                  >
                    {tx(hero.secondaryButtonText, hero.secondaryButtonText_en)}
                  </button>
                )}
              </div>
            </div>
          </section>
        </ScrollReveal>

        {/* ── 2. STATS ── */}
        {statsSection?.stats?.length > 0 && (
          <ScrollReveal>
            <section className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 px-6 lg:px-28 py-8 lg:py-10 bg-white">
              {statsSection.stats.map((s, i) => (
                <div
                  key={i}
                  className="border border-[#e0eeeb] rounded-xl px-4 py-4 lg:px-7 lg:py-6 flex flex-col gap-1.5 lg:gap-2 shadow-sm"
                >
                  <span className="text-[9px] lg:text-xs font-semibold text-[#6b9e96] uppercase tracking-wide">
                    {tx(s.label, s.label_en)}
                  </span>
                  <span className="text-xl lg:text-4xl font-bold text-[#0D3A2F]">
                    {s.value}
                    {(s.small || s.small_en) && (
                      <small className={`text-[10px] lg:text-sm font-normal ml-1 lg:ml-1.5 ${s.smallRenk || 'text-[#4a6b63]'}`}>
                        {tx(s.small, s.small_en)}
                      </small>
                    )}
                  </span>
                  <div className="h-1 bg-[#2a9d8f] rounded-full mt-1 w-full" />
                </div>
              ))}
            </section>
          </ScrollReveal>
        )}

        {/* ── 3. FEATURES ── */}
        {features && (
          <ScrollReveal>
            <section className="px-6 lg:px-28 py-12 lg:py-20 bg-[#f5faf9] text-center">
              <h2 className="text-2xl lg:text-4xl font-black text-[#0D3A2F] mb-4">
                {tx(features.Title, features.Title_en)}
              </h2>
              <p className="text-sm lg:text-[15px] text-[#4a6b63] max-w-[520px] mx-auto mb-10 lg:mb-12 leading-relaxed">
                {tx(features.Description, features.Description_en)}
              </p>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                {(features.cards || []).map((f, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-xl p-5 lg:px-5 lg:py-7 text-left border border-[#e0eeeb] flex flex-col gap-3 hover:shadow-xl transition-all"
                  >
                    <div className="text-[#2a9d8f] scale-90 lg:scale-100 origin-left">
                      <DynamicIcon name={f.icon || 'FaShieldAlt'} size={30} />
                    </div>
                    <h3 className="text-xs lg:text-[15px] font-black text-[#0D3A2F] leading-tight mb-1 lg:mb-2.5">
                      {tx(f.title, f.title_en)}
                    </h3>
                    <p className="hidden sm:block text-[11px] lg:text-sm leading-relaxed text-[#4a6b63]">
                      {tx(f.description, f.description_en)}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          </ScrollReveal>
        )}

        {/* ── 4. CTA (Dikdörtgen Kompakt Form) ── */}
        {cta && (
          <section className="bg-white py-12 lg:py-24 px-6">
            <div className="max-w-[1280px] mx-auto bg-[#2a9d8f] rounded-2xl lg:rounded-[3rem] p-8 lg:p-20 text-center flex flex-col items-center gap-6 lg:gap-10 shadow-2xl relative overflow-hidden">
              <div className="absolute right-0 top-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
              <h2 className="text-2xl lg:text-4xl font-black text-white m-0 leading-tight">
                {tx(cta.Title, cta.Title_en)}
              </h2>

              <p className="text-xs lg:text-lg text-white/85 max-w-[500px] mx-auto m-0 leading-relaxed font-medium">
                {tx(cta.Description, cta.Description_en)}
              </p>

              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <button
                  onClick={() => navigate('/iletisim')}
                  className="h-12 lg:h-14 bg-white text-[#2a9d8f] font-black px-8 rounded-xl text-sm lg:text-base cursor-pointer hover:bg-[#f0faf9] transition-all border-none shadow-lg w-full sm:w-auto"
                >
                  {tx(cta.primaryButtonText, cta.primaryButtonText_en)}
                </button>
                {cta.secondaryButtonText && (
                  <button
                    onClick={() => navigate('/iletisim')}
                    className="h-12 lg:h-14 bg-transparent text-white border-2 border-white px-8 rounded-xl text-sm lg:text-base font-black cursor-pointer hover:bg-white/10 transition-all w-full sm:w-auto"
                  >
                    {tx(cta.secondaryButtonText, cta.secondaryButtonText_en)}
                  </button>
                )}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
};

export default FizikselMedya;